<script lang="ts">
import { inject } from 'vue'
import type Map from 'ol/Map.js'
import type VectorSource from 'ol/source/Vector'
import type { SimpleGeometry } from 'ol/geom'
import Bar from 'ol-ext/control/Bar'
import Toggle from 'ol-ext/control/Toggle'
import Button from 'ol-ext/control/Button'
import DkOlInteractionModify from '../interactions/dk-ol-interaction-modify.vue'
import { VectorLayerService } from '../../../services/vectorLayer.service'

export default {
    name: 'DkOlVectorLayerEditor',
    components: { DkOlInteractionModify },
    props: {
        create: {
            type: Boolean,
            required: false,
            default: false,
        },
        createType: {
            // type: GeometryType,
            type: String,
            required: false,
            default: '',
        },
        allowMultipleGeometry: {
            type: Boolean,
            required: false,
            default: false,
        },
        allowHoleCreation: {
            type: Boolean,
            required: false,
            default: true,
        },
        enableSnap: {
            type: Boolean,
            required: false,
            default: true,
        },
        snapSource: {
            required: false,
            default: null,
        },
        vectorLayerService: {
            type: VectorLayerService,
            required: false,
            default: new VectorLayerService(),
        },
    },
    emits: ['updateGeometry', 'resetGeometry'],
    data() {
        return {
            map: null as Map,
            vectorSource: null as VectorSource | null,
            hasChanged: false as boolean,
            interaction: null as string | null,
            note: null as Notification,
            mainBar: null as Bar,
            mainBarSwitchToggles: {
                Draw: Toggle,
                DrawHole: Toggle | null,
                Modify: Toggle,
                Delete: Toggle,
            },
            mainBarButtons: {
                ResetGeometry: Button,
            },
        }
    },
    computed: {
        geometryType(): GeometryType {
            if (this.createType) {
                return this.createType as GeometryType
            }

            return (this.vectorSource.getFeatures()[0] as Feature).getGeometry().getType()
        },
    },
    watch: {
        create() {
            this.refreshMainBar()
        },
    },
    unmounted(): void {
        this.removeMainBar()
    },
    mounted() {
        this.map = inject('map')
        this.vectorSource = inject('vectorSource')

        this.initMainBar()
    },
    methods: {
        initMainBar(): void {
            this.interaction = this.create ? 'Draw' : 'Modify'

            this.mainBar = new Bar()
            this.map.addControl(this.mainBar)

            let editIcon: string = null
            let addIcon: string = null
            let deleteIcon: string = null
            switch (this.geometryType) {
                case 'Point':
                case 'MultiPoint':
                    editIcon = 'vector-point-edit'
                    addIcon = 'vector-point-plus'
                    deleteIcon = 'vector-point-minus'
                    break

                case 'Polygon':
                case 'MultiPolygon':
                    editIcon = 'vector-square-edit'
                    addIcon = 'vector-square-plus'
                    deleteIcon = 'vector-square-remove'
                    break
                case 'LineString':
                case 'MultiLineString':
                    editIcon = 'vector-polyline-edit'
                    addIcon = 'vector-polyline-plus'
                    deleteIcon = 'vector-polyline-remove'
                    break
            }

            // Draw toggle
            this.mainBarSwitchToggles.Draw = new Toggle({
                html: `<span class="mdi mdi-${addIcon}"></span>`,
                title: 'Ajouter un objet',
                onToggle: (active) => {
                    this.setInteractionType(active, 'Draw')
                },
                // bar: this.mainBar,
                autoActive: true,
                active: this.interaction === 'Draw',
            })
            this.mainBar.addControl(this.mainBarSwitchToggles.Draw)

            // Modify toggle
            this.mainBarSwitchToggles.Modify = new Toggle({
                html: `<span class="mdi mdi-${editIcon}"></span>`,
                title: 'Modifier l\'objet',
                onToggle: (active) => {
                    this.setInteractionType(active, 'Modify')
                },
                // bar: this.mainBar,
                autoActive: true,
                active: this.interaction === 'Modify',
            })
            this.mainBar.addControl(this.mainBarSwitchToggles.Modify)

            if ((this.geometryType as GeometryType).endsWith('Polygon')) {
                // DrawHole Toggle
                this.mainBarSwitchToggles.DrawHole = new Toggle({
                    html: '<span class="mdi mdi-vector-difference"></span>',
                    title: 'Ajouter un trou',
                    onToggle: (active) => {
                        this.setInteractionType(active, 'DrawHole')
                    },
                    autoActive: true,
                    active: false,
                })
                this.mainBar.addControl(this.mainBarSwitchToggles.DrawHole)
            }

            // Delete Toggle
            this.mainBarSwitchToggles.Delete = new Toggle({
                html: `<span class="mdi mdi-${deleteIcon} text-danger"></span>`,
                title: 'Supprimer un objet',
                onToggle: (active) => {
                    this.setInteractionType(active, 'Delete')
                },
                autoActive: true,
                active: false,
            })
            this.mainBar.addControl(this.mainBarSwitchToggles.Delete)

            // resetGeometry Button
            // TODO DEBUG
            // this.mainBarButtons.ResetGeometry = new Button({
            //     html: '<span class="mdi mdi-arrow-u-left-top"></span>',
            //     title: 'Annuler les modifications',
            //     handleClick: () => {
            //         this.resetGeometry()
            //     },
            //
            // })
            // this.mainBar.addControl(this.mainBarButtons.ResetGeometry)

            this.refreshMainBar()
        },
        refreshMainBar(): void {
            const count = this.vectorSource.getFeatures().length
            if (!this.allowMultipleGeometry) {
                (this.mainBarSwitchToggles.Draw as Toggle).setDisable(count > 0)
                if (this.interaction === 'Draw' && count > 0) {
                    this.setInteractionType(false, 'Draw')
                }
            }
        },
        setInteractionType(active: boolean, interactionType: string): void {
            if (active) {
                // turn off other toggle button
                for (const toggleKey in this.mainBarSwitchToggles) {
                    if (toggleKey !== interactionType && this.mainBarSwitchToggles[toggleKey]) {
                        this.mainBarSwitchToggles[toggleKey].setActive(false)
                    }
                }
            }
            this.interaction = active ? interactionType : null
        },
        updateGeometry(geometry: SimpleGeometry) {
            this.hasChanged = true
            this.$emit('updateGeometry', geometry)
            setTimeout(this.refreshMainBar)
        },
        resetGeometry() {
            this.hasChanged = false
            this.$emit('resetGeometry')
            setTimeout(this.refreshMainBar)
        },
        removeMainBar() {
            for (const key in this.mainBarSwitchToggles) {
                if (this.mainBarSwitchToggles[key]) {
                    this.map.removeControl(this.mainBarSwitchToggles[key])
                    delete this.mainBarSwitchToggles[key]
                }
            }
            for (const key in this.mainBarButtons) {
                if (this.mainBarButtons[key]) {
                    this.map.removeControl(this.mainBarButtons[key])
                    delete this.mainBarButtons[key]
                }
            }
            this.map.removeControl(this.mainBar)
            delete this.mainBar
        },
    },
}
</script>

<template>
    <DkOlInteractionModify
        v-if="vectorSource"
        ref="interactions"
        :interaction="interaction"
        :snap-source="snapSource"
        :create="create"
        :create-type="geometryType"
        :allow-multiple-geometry
        @update-geometry="updateGeometry($event)"
    />
</template>

<style>

</style>
